'use client';

import { Alert } from '@vs-shop/ui/alert';
import { Button } from '@vs-shop/ui/button';
import { Checkbox } from '@vs-shop/ui/checkbox';
import { Input } from '@vs-shop/ui/input';
import { captureCause } from '../../shared/utils/sentry';
import { Cause, Exit, Match, Option } from 'effect';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { loginForm } from '../../data/loginForm';

import effectTsResolver from '@vs-shop/engine/adapter/effect-hook-form/effectTsResolver';
import { registerCustomerAtomFn } from '@vs-shop/engine/atom/auth/mutation/registerCustomer';
import { CreateCustomerError } from '@vs-shop/engine/request/backendApi/customer/createCustomer';
import type { RegistrationFormData } from '@vs-shop/schemas/authForms';
import { RegistrationFormSchema } from '@vs-shop/schemas/authForms';
import { getViolationErrorMessage } from '../../effect/utils/error/violationErrorMessage';
import { useAtomSet } from '@effect/atom-react';

type Props = {
  onSuccessAction: () => void;
};

export default function RegistrationForm({ onSuccessAction }: Props) {
  const intl = useIntl();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isPending, startTransition] = useTransition();
  const createCustomerMutation = useAtomSet(registerCustomerAtomFn, { mode: 'promiseExit' });

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    watch,
    setValue,
  } = useForm<RegistrationFormData>({
    resolver: effectTsResolver(RegistrationFormSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (data: RegistrationFormData) => {
    setErrorMessage(null);

    startTransition(async () => {
      const result = await createCustomerMutation({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        subscribedToNewsletter: data.subscribedToNewsletter ?? false,
      });

      Exit.match(result, {
        onFailure: (cause) => {
          captureCause(cause, {
            component: 'RegistrationForm',
            action: 'Registration failed',
            additionalData: {
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              subscribedToNewsletter: data.subscribedToNewsletter ?? false,
            },
          });

          const showUnknownError = () => {
            setErrorMessage(
              intl.formatMessage({
                id: 'component.registrationForm.apiError',
                defaultMessage: 'Došlo k chybě při registraci. Zkuste to prosím znovu.',
              })
            );
          };
          const failReason = cause.reasons.find(Cause.isFailReason);

          if (failReason) {
            Match.value(failReason.error).pipe(
              Match.tag(CreateCustomerError.Tags.Violation, (error) => {
                Option.match(error.firstViolation, {
                  onSome: (firstViolation) => {
                    setErrorMessage(getViolationErrorMessage(firstViolation, intl));
                  },
                  onNone: () => {
                    showUnknownError();
                  },
                });
              }),
              Match.orElse(() => {
                showUnknownError();
              })
            );
          } else {
            showUnknownError();
          }
        },
        onSuccess: (_response) => {
          onSuccessAction();
        },
      });
    });
  };

  const subscribedToNewsletter = watch('subscribedToNewsletter') ?? false;

  const minCharsStr = intl.formatNumber(loginForm.minimumCharacters, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <div>
      {errorMessage && (
        <Alert
          variant="destructive"
          data-slot="registration-form-error"
          className="mb-4"
          role="alert"
        >
          {errorMessage}
        </Alert>
      )}

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...register('email')}
          id="email"
          type="email"
          label={intl.formatMessage({
            id: 'component.registrationForm.email.label',
            defaultMessage: 'E-mail',
          })}
          placeholder={intl.formatMessage({
            id: 'component.registrationForm.email.placeholder',
            defaultMessage: 'E-mail',
          })}
          error={errors.email?.message ?? null}
          isTouched={!!touchedFields.email}
        />

        <div>
          <Input
            {...register('password')}
            id="password"
            type="password"
            label={intl.formatMessage({
              id: 'component.registrationForm.password.label',
              defaultMessage: 'Heslo',
            })}
            placeholder={intl.formatMessage(
              {
                id: 'component.registrationForm.password.placeholder',
                defaultMessage: 'Heslo (min. {minimumCharacters} znaků)',
              },
              { minimumCharacters: minCharsStr }
            )}
            error={errors.password?.message ?? null}
            isTouched={!!touchedFields.password}
          />
        </div>

        <Input
          {...register('firstName')}
          id="firstName"
          label={intl.formatMessage({
            id: 'container.registration.form.firstName',
            defaultMessage: 'Jméno',
          })}
          error={errors.firstName?.message ?? null}
          hasValue={!!watch('firstName')}
          isTouched={!!touchedFields.firstName}
        />

        <Input
          {...register('lastName')}
          id="lastName"
          label={intl.formatMessage({
            id: 'container.registration.form.lastName',
            defaultMessage: 'Příjmení',
          })}
          error={errors.lastName?.message ?? null}
          hasValue={!!watch('lastName')}
          isTouched={!!touchedFields.lastName}
        />

        <label htmlFor="subscribedToNewsletter" className="flex cursor-pointer gap-4">
          <span className="leading-[inherit]">
            <Checkbox
              id="subscribedToNewsletter"
              checked={subscribedToNewsletter}
              onCheckedChange={(checked) => {
                setValue('subscribedToNewsletter', checked);
              }}
            />
          </span>
          <span className="text-foreground-muted">
            {intl.formatMessage({
              id: 'container.registration.form.subscribedToNewsletter.heading',
              defaultMessage: 'Odebírejte zprávy od RC profíků a buďte v obraze!',
            })}
          </span>
        </label>

        <div className="pt-4">
          <Button
            type="submit"
            variant="primary"
            className="w-full uppercase"
            loading={isPending}
            disabled={isPending}
          >
            {intl.formatMessage({
              id: 'component.registrationForm.button.submit',
              defaultMessage: 'Registrovat',
            })}
          </Button>
        </div>
      </form>
    </div>
  );
}
